import { API_BASE_URL, apiFetch, staticFetch, TEMPLATES_BASE_URL, getMySites, publishSite } from "../../api.js";

const isAbsoluteUrl = (value) => /^https?:\/\//i.test(String(value || "").trim());

const joinBaseUrl = (baseUrl, assetPath) => {
    const rawPath = String(assetPath || "").trim();
    if (!rawPath) return "";
    if (isAbsoluteUrl(rawPath)) return rawPath;

    const cleanBase = String(baseUrl || "").replace(/\/+$/, "");
    const cleanPath = rawPath.startsWith("/") ? rawPath : `/${rawPath}`;
    return `${cleanBase}${cleanPath}`;
};

const withQueryFlag = (url, key, value) => {
    if (!url) return "";
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
};

const cloneValue = (value) => JSON.parse(JSON.stringify(value || {}));
const OPTIONAL_SECTION_KEY_PREFIXES = ["sectionVisibility:", "sectionOrder:"];

const extractOptionalSectionState = (value) => {
    const source = value && typeof value === "object" ? value : {};
    const next = {};

    for (const [field, entry] of Object.entries(source)) {
        const key = String(field || "").trim();
        if (!OPTIONAL_SECTION_KEY_PREFIXES.some((prefix) => key.startsWith(prefix))) continue;
        next[key] = cloneValue(entry);
    }

    return next;
};

const withOptionalSectionState = (url, value) => {
    const rawUrl = String(url || "").trim();
    if (!rawUrl) return rawUrl;

    const optionalSections = extractOptionalSectionState(value);
    if (!Object.keys(optionalSections).length) return rawUrl;

    try {
        const nextUrl = new URL(rawUrl, window.location.origin);
        nextUrl.searchParams.set("heyweb_sections", JSON.stringify(optionalSections));
        return nextUrl.toString();
    } catch {
        return rawUrl;
    }
};

const isInlineImageDataUrl = (value) =>
    typeof value === "string" &&
    /^data:image\/[a-z0-9.+-]+;base64,/i.test(value.trim());

const loadImageElement = (src) =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error("Nao foi possivel processar a imagem antes de guardar."));
        image.src = src;
    });

const MAX_INLINE_IMAGE_LENGTH = 180000;
const MAX_BACKUP_TEXT_LENGTH = 4000;

const canvasToDataUrl = (canvas, mimeType, quality) =>
    new Promise((resolve, reject) => {
        try {
            resolve(canvas.toDataURL(mimeType, quality));
        } catch (error) {
            reject(error);
        }
    });

const optimizeInlineImageDataUrl = async (dataUrl) => {
    const raw = String(dataUrl || "").trim();
    if (!isInlineImageDataUrl(raw) || raw.length <= MAX_INLINE_IMAGE_LENGTH) {
        return raw;
    }

    const source = await loadImageElement(raw);
    const steps = [
        { maxWidth: 1280, maxHeight: 1280, mimeType: "image/webp", quality: 0.76, maxLength: 280000 },
        { maxWidth: 960, maxHeight: 960, mimeType: "image/webp", quality: 0.68, maxLength: 220000 },
        { maxWidth: 768, maxHeight: 768, mimeType: "image/webp", quality: 0.6, maxLength: 180000 },
        { maxWidth: 560, maxHeight: 560, mimeType: "image/webp", quality: 0.52, maxLength: 120000 },
        { maxWidth: 420, maxHeight: 420, mimeType: "image/webp", quality: 0.46, maxLength: 90000 },
    ];
    let bestCandidate = raw;

    for (const step of steps) {
        const scale = Math.min(
            1,
            step.maxWidth / Math.max(source.naturalWidth || 1, 1),
            step.maxHeight / Math.max(source.naturalHeight || 1, 1)
        );
        const width = Math.max(1, Math.round((source.naturalWidth || 1) * scale));
        const height = Math.max(1, Math.round((source.naturalHeight || 1) * scale));
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        const context = canvas.getContext("2d");
        if (!context) {
            break;
        }

        context.clearRect(0, 0, width, height);
        context.drawImage(source, 0, 0, width, height);

        const optimized = await canvasToDataUrl(canvas, step.mimeType, step.quality);
        if (!bestCandidate || optimized.length < bestCandidate.length) {
            bestCandidate = optimized;
        }
        if (optimized.length <= step.maxLength) {
            return optimized;
        }
    }

    return bestCandidate;
};

const optimizeEditorPayload = async (value) => {
    if (Array.isArray(value)) {
        const nextItems = [];
        for (const item of value) {
            nextItems.push(await optimizeEditorPayload(item));
        }
        return nextItems;
    }

    if (isInlineImageDataUrl(value)) {
        return optimizeInlineImageDataUrl(value);
    }

    if (!value || typeof value !== "object") {
        return value;
    }

    const nextObject = {};
    for (const [key, entry] of Object.entries(value)) {
        nextObject[key] = await optimizeEditorPayload(entry);
    }
    return nextObject;
};

const buildEditorBackup = (value) => {
    if (Array.isArray(value)) {
        const nextItems = value
            .map((item) => buildEditorBackup(item))
            .filter((item) => item !== undefined);
        return nextItems.length ? nextItems : undefined;
    }

    if (typeof value === "string") {
        if (isInlineImageDataUrl(value)) return undefined;
        return value.length > MAX_BACKUP_TEXT_LENGTH ? value.slice(0, MAX_BACKUP_TEXT_LENGTH) : value;
    }

    if (!value || typeof value !== "object") {
        return value;
    }

    const nextObject = {};
    for (const [key, entry] of Object.entries(value)) {
        const nextValue = buildEditorBackup(entry);
        if (nextValue !== undefined) {
            nextObject[key] = nextValue;
        }
    }

    return Object.keys(nextObject).length ? nextObject : undefined;
};

const isSharedStateField = (field) => {
    const key = String(field || "").trim().toLowerCase();
    if (!key) return false;
    if (key.startsWith("header:global:")) return true;
    if (key.startsWith("banner:")) return true;
    if (key.startsWith("logo:")) return true;
    if (key.startsWith("i18n:header.")) return true;
    if (key.startsWith("i18n:search.")) return true;
    if (key.startsWith("i18n:locale.")) return true;
    if (key.includes(":headerglobal:")) return true;
    return false;
};

const extractSharedState = (value) => {
    const source = value && typeof value === "object" ? value : {};
    const next = {};

    for (const [field, entry] of Object.entries(source)) {
        if (!isSharedStateField(field)) continue;
        next[field] = cloneValue(entry);
    }

    return next;
};

const mergeEditorState = (pageContent, editorBackup) => {
    const safePageContent = pageContent && typeof pageContent === "object" ? cloneValue(pageContent) : {};
    const safeBackup = extractSharedState(editorBackup);
    const next = { ...safePageContent };

    Object.entries(safeBackup).forEach(([field, entry]) => {
        if (
            entry && typeof entry === "object" && !Array.isArray(entry) &&
            next[field] && typeof next[field] === "object" && !Array.isArray(next[field])
        ) {
            next[field] = {
                ...next[field],
                ...entry,
            };
            return;
        }

        next[field] = entry;
    });

    return next;
};

const getTemplateIdentity = (template) =>
    template?.manifest?.id ||
    template?.id ||
    template?.template_id ||
    template?.templateId ||
    template?.slug ||
    template?.vertical ||
    template?.name ||
    template?.title ||
    "";

const getTemplateCategory = (template) =>
    template?.categoria ||
    template?.category ||
    template?.manifest?.category ||
    "";

const getTemplateVertical = (template) =>
    template?.vertical ||
    template?.slug ||
    template?.name ||
    template?.title ||
    template?.manifest?.id ||
    "";

const normalizeTemplateCollection = (catalog) => (Array.isArray(catalog) ? catalog : []);

const normalizeRoutePath = (value) => {
    const raw = String(value || "").trim();
    if (!raw || raw === "/") return "/";
    const clean = raw.replace(/^\/+|\/+$/g, "");
    return clean ? `/${clean}/` : "/";
};

const findTemplateInCatalog = (catalog, matcher) =>
    normalizeTemplateCollection(catalog).find((template) => matcher(template)) || null;

const parseResponseBody = async (response) => {
    const text = await response.text();
    if (!text) return null;

    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
};

const ensureOk = async (response, fallbackMessage) => {
    if (response.ok) {
        return parseResponseBody(response);
    }

    const body = await parseResponseBody(response);
    const message =
        body?.stderr ||
        body?.message ||
        body?.error ||
        (typeof body === "string" ? body : "") ||
        fallbackMessage ||
        `HTTP ${response.status}`;

    throw new Error(message);
};

const inferHeroTitle = (currentData, siteName, selectedTemplate) =>
    currentData?.hero?.title ||
    currentData?.hero_title ||
    currentData?.title ||
    selectedTemplate?.home?.hero?.title ||
    selectedTemplate?.home?.hero_title ||
    siteName ||
    "Novo Site";

const inferHeroSubtitle = (currentData, selectedTemplate) =>
    currentData?.hero?.subtitle ||
    currentData?.hero_subtitle ||
    currentData?.subtitle ||
    selectedTemplate?.home?.hero?.subtitle ||
    selectedTemplate?.home?.hero_subtitle ||
    undefined;

const buildCreatePayload = ({ templateId, siteName, slug, currentData, selectedTemplate }) => ({
    template_id: templateId,
    site_name: siteName,
    path: slug,
    slug,
    hero_title: inferHeroTitle(currentData, siteName, selectedTemplate),
    hero_subtitle: inferHeroSubtitle(currentData, selectedTemplate),
    editor_content: buildEditorBackup(extractSharedState(currentData)) || {},
});

const buildUpdatePayload = ({ siteName, slug, currentData, selectedTemplate }) => ({
    site_name: siteName,
    path: slug,
    slug,
    hero_title: inferHeroTitle(currentData, siteName, selectedTemplate),
    hero_subtitle: inferHeroSubtitle(currentData, selectedTemplate),
    editor_content: buildEditorBackup(extractSharedState(currentData)) || {},
});

const buildPageSlugFromRoute = (routePath) => {
    const normalized = normalizeRoutePath(routePath);
    if (normalized === "/") return "home";

    return normalized
        .replace(/^\/|\/$/g, "")
        .replace(/\/+/g, "--")
        .replace(/[^a-z0-9-]/gi, "-")
        .replace(/-+/g, "-")
        .replace(/^-+|-+$/g, "")
        .toLowerCase() || "page";
};

const buildTemplatePageDescriptors = async (previewUrl) => {
    if (!previewUrl) return [{ route: "/", slug: "home", title: "Home", body: "" }];

    const visited = new Set();
    const descriptors = new Map();
    const queue = [previewUrl];
    const rootUrl = new URL(previewUrl);
    const rootPath = normalizeRoutePath(rootUrl.pathname);
    const rootPrefix = rootPath === "/" ? "/" : rootPath;
    const maxPages = 40;

    const toRelativeRoute = (url) => {
        const pathname = normalizeRoutePath(url.pathname);
        if (pathname === rootPrefix) return "/";
        if (!pathname.startsWith(rootPrefix)) return null;
        const suffix = pathname.slice(rootPrefix.length - 1);
        return normalizeRoutePath(suffix);
    };

    const isAllowedRoute = (url) => {
        if (url.origin !== rootUrl.origin) return false;
        const route = toRelativeRoute(url);
        return route !== null;
    };

    while (queue.length && descriptors.size < maxPages) {
        const currentUrl = queue.shift();
        if (!currentUrl || visited.has(currentUrl)) continue;
        visited.add(currentUrl);

        try {
            const response = await staticFetch(currentUrl);
            if (!response.ok) continue;

            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            const current = new URL(currentUrl);
            const route = toRelativeRoute(current);
            if (!route) continue;

            descriptors.set(route, {
                route,
                slug: buildPageSlugFromRoute(route),
                title: String(doc.title || "").trim() || (route === "/" ? "Home" : route),
                body: null,
            });

            doc.querySelectorAll("a[href]").forEach((link) => {
                const href = String(link.getAttribute("href") || "").trim();
                if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return;

                try {
                    const nextUrl = new URL(href, current.href);
                    nextUrl.hash = "";
                    nextUrl.search = "";
                    if (!isAllowedRoute(nextUrl)) return;
                    const nextHref = nextUrl.toString();
                    if (!visited.has(nextHref)) {
                        queue.push(nextHref);
                    }
                } catch {
                    // ignore invalid links
                }
            });
        } catch (error) {
            console.warn("Nao foi possivel indexar a pagina do template:", currentUrl, error);
        }
    }

    if (!descriptors.has("/")) {
        descriptors.set("/", {
            route: "/",
            slug: "home",
            title: "Home",
            body: "",
        });
    }

    return [...descriptors.values()];
};

const upsertSitePage = async (sitePath, page) => {
    const payload = {
        title: page.title,
        layout: page.slug === "home" ? "home" : "page",
        route: page.route,
        status: "published",
        content: page.content,
    };

    if (typeof page.body === "string") {
        payload.body = page.body;
    }

    let updateResponse = await apiFetch(`/my-sites/${encodeURIComponent(sitePath)}/pages/${encodeURIComponent(page.slug)}`, {
        method: "PUT",
        body: payload,
    });

    if (!updateResponse.ok) {
        const createResponse = await apiFetch(`/my-sites/${encodeURIComponent(sitePath)}/pages`, {
            method: "POST",
            body: {
                slug: page.slug,
                ...payload,
            },
        });
        await ensureOk(createResponse, `Nao foi possivel criar a pagina ${page.slug}.`);
        updateResponse = await apiFetch(`/my-sites/${encodeURIComponent(sitePath)}/pages/${encodeURIComponent(page.slug)}`, {
            method: "PUT",
            body: payload,
        });
    }

    await ensureOk(updateResponse, `Nao foi possivel guardar a pagina ${page.slug}.`);
};

const requestPreviewStateSnapshot = (iframe, previewWin) =>
    new Promise((resolve) => {
        if (!iframe || !previewWin) {
            resolve(null);
            return;
        }

        let settled = false;
        const timeoutId = window.setTimeout(() => {
            if (settled) return;
            settled = true;
            resolve(null);
        }, 700);

        const handleMessage = (event) => {
            if (event.source !== iframe.contentWindow) return;
            if (!event.data || event.data.type !== "HEYWEB_STATE") return;

            window.removeEventListener("message", handleMessage);
            window.clearTimeout(timeoutId);
            settled = true;
            resolve(event.data.data && typeof event.data.data === "object" ? event.data.data : null);
        };

        window.addEventListener("message", handleMessage);
        previewWin.postMessage({ type: "HEYWEB_REQUEST_STATE" }, "*");
    });

const createEditorSnapshot = (siteName, slug, currentData) => ({
    siteName: String(siteName || "").trim(),
    slug: String(slug || "").trim(),
    content: cloneValue(currentData),
});

const countSnapshotLeaves = (value) => {
    if (Array.isArray(value)) {
        return value.reduce((total, item) => total + countSnapshotLeaves(item), 0);
    }

    if (!value || typeof value !== "object") {
        return 1;
    }

    const entries = Object.values(value);
    if (!entries.length) return 1;
    return entries.reduce((total, entry) => total + countSnapshotLeaves(entry), 0);
};

const countSnapshotDifferences = (currentValue, baselineValue) => {
    if (Array.isArray(currentValue) || Array.isArray(baselineValue)) {
        const currentItems = Array.isArray(currentValue) ? currentValue : [];
        const baselineItems = Array.isArray(baselineValue) ? baselineValue : [];
        const maxLength = Math.max(currentItems.length, baselineItems.length);
        let total = 0;

        for (let index = 0; index < maxLength; index += 1) {
            const hasCurrent = index < currentItems.length;
            const hasBaseline = index < baselineItems.length;

            if (!hasCurrent && hasBaseline) {
                total += countSnapshotLeaves(baselineItems[index]);
                continue;
            }

            if (hasCurrent && !hasBaseline) {
                total += countSnapshotLeaves(currentItems[index]);
                continue;
            }

            total += countSnapshotDifferences(currentItems[index], baselineItems[index]);
        }

        return total;
    }

    const currentIsObject = currentValue && typeof currentValue === "object";
    const baselineIsObject = baselineValue && typeof baselineValue === "object";
    if (currentIsObject || baselineIsObject) {
        const currentObject = currentIsObject ? currentValue : {};
        const baselineObject = baselineIsObject ? baselineValue : {};
        const keys = new Set([...Object.keys(currentObject), ...Object.keys(baselineObject)]);
        let total = 0;

        keys.forEach((key) => {
            const hasCurrent = Object.prototype.hasOwnProperty.call(currentObject, key);
            const hasBaseline = Object.prototype.hasOwnProperty.call(baselineObject, key);

            if (!hasCurrent && hasBaseline) {
                total += countSnapshotLeaves(baselineObject[key]);
                return;
            }

            if (hasCurrent && !hasBaseline) {
                total += countSnapshotLeaves(currentObject[key]);
                return;
            }

            total += countSnapshotDifferences(currentObject[key], baselineObject[key]);
        });

        return total;
    }

    return Object.is(currentValue, baselineValue) ? 0 : 1;
};

const fetchPublishedSnapshot = async (sitePath) => {
    const safePath = String(sitePath || "").trim();
    if (!safePath) return null;

    try {
        const [siteResponse, homePageResponse] = await Promise.all([
            fetch(`${API_BASE_URL}/api/public/sites/${encodeURIComponent(safePath)}`, {
                method: "GET",
                headers: { Accept: "application/json" },
            }),
            fetch(`${API_BASE_URL}/api/public/sites/${encodeURIComponent(safePath)}/pages/home`, {
                method: "GET",
                headers: { Accept: "application/json" },
            }),
        ]);

        if (!siteResponse.ok || !homePageResponse.ok) {
            return null;
        }

        const site = await parseResponseBody(siteResponse);
        const homePage = await parseResponseBody(homePageResponse);
        return createEditorSnapshot(
            site?.name || safePath,
            site?.path || safePath,
            mergeEditorState(homePage?.content, site?.extra?.editor_content)
        );
    } catch (error) {
        console.warn("Nao foi possivel carregar o snapshot publicado do site:", error);
        return null;
    }
};

export const EditorView = {
    state: {
        mode: null,
        categoria: null,
        vertical: null,
        slug: null,
        sitePath: null,
        templateId: null,
        currentData: {},
        previewReady: false,
        previewWin: null,
        selectedTemplate: null,
    },

    render: async () => {
        return `
            <div class="editor-shell animate-fade-in">
                <div class="editor-stack">
                    <section class="editor-toolbar">
                        <div class="editor-toolbar-bar">
                            <div class="editor-toolbar-meta">
                                <div class="editor-toolbar-label">Live Editor</div>
                                <h1 id="ed-site-title" class="editor-site-title">Editor de Site</h1>
                                <p id="ed-subtitle" class="editor-site-subtitle">Edicao visual em tempo real para o motor Eleventy.</p>
                            </div>

                            <div class="editor-toolbar-actions">
                                <button id="btn-open-settings" class="editor-config-button" type="button">
                                    <span class="editor-settings-toggle-icon"><i data-lucide="settings-2"></i></span>
                                    Configuracao
                                </button>
                                <button id="btn-refresh-preview" type="button" style="height:2.9rem; padding:0 1.05rem; border-radius:0.9rem; border:1px solid #dbe4f0; background:white; color:#0f172a; font-weight:800; cursor:pointer; box-shadow:0 10px 24px rgba(148,163,184,0.08);">
                                    Atualizar
                                </button>
                                <button id="btn-cancel" type="button" style="height:2.9rem; padding:0 1.05rem; border-radius:0.9rem; border:1px solid #dbe4f0; background:#f8fafc; color:#334155; font-weight:800; cursor:pointer;">
                                    Cancelar
                                </button>
                                <button id="btn-save" type="button" style="height:2.9rem; padding:0 1.2rem; border-radius:0.9rem; border:none; background:linear-gradient(135deg, #2563eb, #60a5fa); color:white; font-weight:900; cursor:pointer; box-shadow:0 16px 34px rgba(37,99,235,0.28);">
                                    Guardar Rascunho
                                </button>
                                <button id="btn-publish" type="button" style="height:2.9rem; padding:0 1.2rem; border-radius:0.9rem; border:none; background:linear-gradient(135deg, #0f766e, #14b8a6); color:white; font-weight:900; cursor:pointer; box-shadow:0 16px 34px rgba(13,148,136,0.26);">
                                    Publicar Site
                                </button>
                            </div>
                        </div>
                    </section>

                    <section class="editor-workspace" id="editor-workspace">
                        <aside class="editor-settings-panel" id="editor-settings-panel">
                            <div class="editor-settings-head">
                                <div>
                                    <h2 class="editor-settings-title">Configuracao do site</h2>
                                    <p class="editor-settings-copy">Ajuste os dados base do projeto sem sair do editor.</p>
                                </div>
                                <button id="btn-close-settings" class="editor-panel-close" type="button" aria-label="Fechar configuracao">
                                    <span class="editor-panel-close-icon"><i data-lucide="x"></i></span>
                                </button>
                            </div>

                            <div class="editor-settings-body">
                                <div class="editor-field">
                                    <label for="site-name">Nome do Site</label>
                                    <input id="site-name" type="text" />
                                </div>

                                <div class="editor-field">
                                    <label for="site-slug">Slug</label>
                                    <input id="site-slug" type="text" />
                                </div>

                                <div class="editor-status-pill">
                                    <span id="preview-status-dot" style="width:0.6rem; height:0.6rem; border-radius:50%; background:#64748b; box-shadow:0 0 0 4px rgba(148,163,184,0.12);"></span>
                                    <span id="preview-sub">A conectar...</span>
                                </div>
                            </div>
                        </aside>

                        <section class="editor-preview-section">
                            <div class="editor-preview-frame-shell" style="overflow:hidden; border:1px solid #dbe4f0; background:#e2e8f0; box-shadow:inset 0 1px 0 rgba(255,255,255,0.7);">
                                <iframe id="site-preview" class="editor-preview-frame"></iframe>
                            </div>
                        </section>
                    </section>
                </div>
            </div>
        `;
    },

    afterRender: async (params) => {
        const mode = params[0];
        let isNewMode = mode === "new";
        let isExistingSiteMode = mode === "site";

        if (!isNewMode && !isExistingSiteMode) {
            window.location.hash = "#templates";
            return;
        }

        const iframe = document.getElementById("site-preview");
        const previewSub = document.getElementById("preview-sub");
        const previewDot = document.getElementById("preview-status-dot");
        const siteNameInput = document.getElementById("site-name");
        const slugInput = document.getElementById("site-slug");
        const btnSave = document.getElementById("btn-save");
        const btnPublish = document.getElementById("btn-publish");
        const btnCancel = document.getElementById("btn-cancel");
        const btnRefresh = document.getElementById("btn-refresh-preview");
        const btnOpenSettings = document.getElementById("btn-open-settings");
        const btnCloseSettings = document.getElementById("btn-close-settings");
        const workspaceEl = document.getElementById("editor-workspace");
        const siteTitleEl = document.getElementById("ed-site-title");
        const subtitleEl = document.getElementById("ed-subtitle");

        let catalog = [];
        let selectedTemplate = null;
        let currentSite = null;
        let currentSitePath = null;
        let categoria = isNewMode ? params[1] : null;
        let vertical = isNewMode ? params[2] : null;

        try {
            const catalogResponse = await staticFetch(`/templates.json?v=${Date.now()}`);
            if (catalogResponse.ok) {
                catalog = normalizeTemplateCollection(await catalogResponse.json());
            }
        } catch (error) {
            console.error("Erro ao carregar catalogo de templates:", error);
        }

        if (isNewMode) {
            if (!categoria || !vertical) {
                window.location.hash = "#templates";
                return;
            }

            selectedTemplate = findTemplateInCatalog(catalog, (template) => {
                return getTemplateCategory(template) === categoria && getTemplateVertical(template) === vertical;
            });
        } else {
            currentSitePath = decodeURIComponent(params[1] || "").trim();
            if (!currentSitePath) {
                window.location.hash = "#dashboard";
                return;
            }

            try {
                const listedSite = (await getMySites()).find((site) => site?.path === currentSitePath) || null;
                const siteResponse = await apiFetch(`/my-sites/${encodeURIComponent(currentSitePath)}`, { method: "GET" });
                const fetchedSite = await ensureOk(siteResponse, "Nao foi possivel carregar o site.");
                currentSite = {
                    ...(listedSite || {}),
                    ...(fetchedSite || {}),
                };
            } catch (error) {
                console.error("Erro ao carregar site existente:", error);
                alert("Nao foi possivel carregar o projeto para edicao.");
                window.location.hash = "#dashboard";
                return;
            }

            selectedTemplate = findTemplateInCatalog(catalog, (template) => {
                const templateId = getTemplateIdentity(template);
                return (
                    templateId === currentSite?.template_id ||
                    getTemplateVertical(template) === currentSite?.template_id ||
                    getTemplateVertical(template) === currentSite?.path
                );
            });

            categoria = getTemplateCategory(selectedTemplate) || currentSite?.categoria || currentSite?.category || "geral";
            vertical = getTemplateVertical(selectedTemplate) || currentSite?.template_id || currentSite?.path;
        }

        let currentData = {};
        let siteName = selectedTemplate?.name || selectedTemplate?.home?.siteName || vertical || "Novo Site";
        let siteSlug = selectedTemplate?.slug || vertical || "novo-site";
        let templateId = getTemplateIdentity(selectedTemplate) || vertical || "";

        if (isExistingSiteMode && currentSite) {
            siteName = currentSite?.name || siteName;
            siteSlug = currentSite?.path || siteSlug;
            currentSitePath = currentSite?.path || currentSitePath;
            templateId = currentSite?.template_id || templateId;
            currentData = cloneValue(currentSite?.extra?.editor_content || {});

            try {
                const homePageResponse = await apiFetch(`/my-sites/${encodeURIComponent(currentSitePath)}/pages/home`, { method: "GET" });
                const homePage = await ensureOk(homePageResponse, "Nao foi possivel carregar a pagina home.");
                currentData = mergeEditorState(homePage?.content, currentSite?.extra?.editor_content);
            } catch (error) {
                console.warn("Home page nao encontrada ou sem conteudo persistido:", error);
            }
        }

        let lastSavedSnapshot = createEditorSnapshot(siteName, siteSlug, currentData);
        let lastPublishedSnapshot = currentSite?.published
            ? (await fetchPublishedSnapshot(currentSitePath || siteSlug)) || cloneValue(lastSavedSnapshot)
            : cloneValue(lastSavedSnapshot);

        EditorView.state.mode = mode;
        EditorView.state.categoria = categoria;
        EditorView.state.vertical = vertical;
        EditorView.state.slug = siteSlug;
        EditorView.state.sitePath = currentSitePath;
        EditorView.state.templateId = templateId;
        EditorView.state.selectedTemplate = selectedTemplate;
        EditorView.state.currentData = cloneValue(currentData);

        siteNameInput.value = siteName;
        slugInput.value = siteSlug;

        if (siteTitleEl) {
            siteTitleEl.innerText = siteName;
        }

        if (subtitleEl) {
            subtitleEl.innerText =
                selectedTemplate?.summary ||
                selectedTemplate?.home?.title ||
                (isExistingSiteMode
                    ? `Edicao visual do site ${siteName}.`
                    : `Edicao visual do template ${vertical}.`);
        }

        const previewPath =
            selectedTemplate?.preview_url ||
            selectedTemplate?.url ||
            currentSite?.url ||
            `/${siteSlug}/`;
        const templateBasePreviewUrl = joinBaseUrl(TEMPLATES_BASE_URL, previewPath);
        const previewUrl = withOptionalSectionState(withQueryFlag(templateBasePreviewUrl, "heyweb", "1"), EditorView.state.currentData);
        iframe.src = previewUrl;

        iframe.onload = () => {
            previewSub.innerText = "Template carregado";
            previewDot.style.background = "#10b981";
        };

        const handleMessage = (event) => {
            if (event.origin !== new URL(TEMPLATES_BASE_URL).origin) return;

            const msg = event.data;
            if (msg.type === "HEYWEB_READY") {
                EditorView.state.previewReady = true;
                EditorView.state.previewWin = iframe.contentWindow;
                previewSub.innerText = "Live Preview Ativo";
                previewDot.style.background = "#10b981";

                EditorView.state.previewWin.postMessage(
                    {
                        type: "HEYWEB_INIT",
                        data: EditorView.state.currentData,
                    },
                    "*"
                );
            }

            if (msg.type === "HEYWEB_UPDATE") {
                EditorView.state.currentData[msg.field] = msg.value;
                updateActionButtons();
                return;
            }

            if (msg.type === "HEYWEB_STATE" && msg.data && typeof msg.data === "object") {
                EditorView.state.currentData = {
                    ...EditorView.state.currentData,
                    ...cloneValue(msg.data),
                };
                updateActionButtons();
            }
        };

        window.addEventListener("message", handleMessage);

        btnRefresh.onclick = () => {
            previewSub.innerText = "A atualizar...";
            previewDot.style.background = "#64748b";
            iframe.src = withQueryFlag(previewUrl, "t", Date.now());
        };

        btnCancel.onclick = () => {
            window.location.hash = isExistingSiteMode ? "#dashboard" : "#templates";
        };

        const setSettingsOpen = (open) => {
            if (!workspaceEl) return;
            workspaceEl.classList.toggle("is-settings-open", open);
        };

        if (btnOpenSettings) {
            btnOpenSettings.onclick = () => {
                setSettingsOpen(!workspaceEl.classList.contains("is-settings-open"));
            };
        }

        if (btnCloseSettings) {
            btnCloseSettings.onclick = () => {
                setSettingsOpen(false);
            };
        }

        siteNameInput.addEventListener("input", () => {
            if (siteTitleEl) {
                siteTitleEl.innerText = siteNameInput.value.trim() || siteName;
            }
            updateActionButtons();
        });

        slugInput.addEventListener("input", () => {
            updateActionButtons();
        });

        const buttonThemes = {
            saveIdle: {
                background: "linear-gradient(135deg, #2563eb, #60a5fa)",
                boxShadow: "0 16px 34px rgba(37,99,235,0.28)",
                color: "white",
                border: "none",
                opacity: "1",
            },
            publishIdle: {
                background: "linear-gradient(135deg, #0f766e, #14b8a6)",
                boxShadow: "0 16px 34px rgba(13,148,136,0.26)",
                color: "white",
                border: "none",
                opacity: "1",
            },
            success: {
                background: "linear-gradient(135deg, #15803d, #4ade80)",
                boxShadow: "0 16px 34px rgba(34,197,94,0.22)",
                color: "white",
                border: "none",
                opacity: "1",
            },
            disabled: {
                background: "#e2e8f0",
                boxShadow: "none",
                color: "#64748b",
                border: "1px solid #cbd5e1",
                opacity: "1",
            },
        };

        const applyButtonTheme = (button, theme) => {
            if (!button || !theme) return;
            button.style.background = theme.background;
            button.style.boxShadow = theme.boxShadow;
            button.style.color = theme.color;
            button.style.border = theme.border;
            button.style.opacity = theme.opacity;
        };

        const setButtonLabel = (button, label, showSpinner = false) => {
            if (!button) return;

            if (showSpinner) {
                button.innerHTML = `<span class="editor-action-inline"><span class="editor-action-spinner" aria-hidden="true"></span><span>${label}</span></span>`;
                button.classList.add("is-loading");
                return;
            }

            button.textContent = label;
            button.classList.remove("is-loading");
        };

        const getLiveSnapshot = () =>
            createEditorSnapshot(
                siteNameInput?.value.trim() || siteName,
                slugInput?.value.trim() || siteSlug,
                EditorView.state.currentData
            );

        const formatCountLabel = (count, singular, plural = singular) => `${count} ${count === 1 ? singular : plural}`;

        const getPendingCounts = () => {
            const currentSnapshot = getLiveSnapshot();
            return {
                unsavedChangesCount: countSnapshotDifferences(currentSnapshot, lastSavedSnapshot),
                unpublishedChangesCount: countSnapshotDifferences(lastSavedSnapshot, lastPublishedSnapshot),
            };
        };

        let currentButtonBusyState = {
            save: null,
            publish: null,
        };
        const updateActionButtons = ({
            saveBusy = false,
            saveLabel = "",
            clearSaveBusy = false,
            publishBusy = false,
            publishLabel = "",
            clearPublishBusy = false,
        } = {}) => {
            if (saveBusy) {
                currentButtonBusyState.save = { label: saveLabel };
            } else if (clearSaveBusy) {
                currentButtonBusyState.save = null;
            }

            if (publishBusy) {
                currentButtonBusyState.publish = { label: publishLabel };
            } else if (clearPublishBusy) {
                currentButtonBusyState.publish = null;
            }

            const { unsavedChangesCount, unpublishedChangesCount } = getPendingCounts();

            if (btnSave) {
                if (currentButtonBusyState.save) {
                    btnSave.disabled = true;
                    setButtonLabel(btnSave, currentButtonBusyState.save.label || "A guardar...", true);
                    applyButtonTheme(btnSave, buttonThemes.saveIdle);
                } else if (unsavedChangesCount > 0) {
                    btnSave.disabled = false;
                    setButtonLabel(btnSave, `${formatCountLabel(unsavedChangesCount, "alteracao", "alteracoes")} para guardar`);
                    applyButtonTheme(btnSave, buttonThemes.saveIdle);
                } else {
                    btnSave.disabled = true;
                    setButtonLabel(btnSave, "Alteracoes guardadas");
                    applyButtonTheme(btnSave, buttonThemes.success);
                }
            }

            if (btnPublish) {
                if (currentButtonBusyState.publish) {
                    btnPublish.disabled = true;
                    setButtonLabel(btnPublish, currentButtonBusyState.publish.label || "A publicar...", true);
                    applyButtonTheme(btnPublish, buttonThemes.publishIdle);
                } else if (unsavedChangesCount > 0) {
                    btnPublish.disabled = true;
                    setButtonLabel(btnPublish, "Guarde para publicar");
                    applyButtonTheme(btnPublish, buttonThemes.disabled);
                } else if (unpublishedChangesCount > 0) {
                    btnPublish.disabled = false;
                    setButtonLabel(btnPublish, `${formatCountLabel(unpublishedChangesCount, "atualizacao", "atualizacoes")} para publicar`);
                    applyButtonTheme(btnPublish, buttonThemes.publishIdle);
                } else {
                    btnPublish.disabled = true;
                    setButtonLabel(btnPublish, "Tudo publicado");
                    applyButtonTheme(btnPublish, buttonThemes.success);
                }
            }
        };

        let statusResetTimer = null;
        const showEditorStatus = (message, tone = "neutral") => {
            if (!previewSub || !previewDot) return;

            if (statusResetTimer) {
                window.clearTimeout(statusResetTimer);
                statusResetTimer = null;
            }

            previewSub.innerText = message;

            if (tone === "success") {
                previewDot.style.background = "#10b981";
            } else if (tone === "error") {
                previewDot.style.background = "#ef4444";
            } else {
                previewDot.style.background = "#64748b";
            }

            if (tone !== "neutral") {
                statusResetTimer = window.setTimeout(() => {
                    previewSub.innerText = "Live Preview Ativo";
                    previewDot.style.background = "#10b981";
                    statusResetTimer = null;
                }, 2800);
            }
        };

        updateActionButtons();

        const persistDraft = async () => {
            const nextSiteName = siteNameInput.value.trim() || siteName;
            const nextSlug = slugInput.value.trim() || siteSlug;

            if (!nextSlug) {
                throw new Error("O slug do site e obrigatorio.");
            }

            const latestPreviewState = await requestPreviewStateSnapshot(iframe, EditorView.state.previewWin);
            if (latestPreviewState && typeof latestPreviewState === "object") {
                EditorView.state.currentData = {
                    ...EditorView.state.currentData,
                    ...cloneValue(latestPreviewState),
                };
            }

            EditorView.state.currentData = await optimizeEditorPayload(EditorView.state.currentData);

            let activeSitePath = EditorView.state.sitePath;
            let persistedSite = currentSite ? { ...currentSite } : null;

            if (isNewMode) {
                const createResponse = await apiFetch("/my-sites", {
                    method: "POST",
                    body: buildCreatePayload({
                        templateId: EditorView.state.templateId,
                        siteName: nextSiteName,
                        slug: nextSlug,
                        currentData: EditorView.state.currentData,
                        selectedTemplate,
                    }),
                });

                const createdSite = await ensureOk(createResponse, "Nao foi possivel criar o site.");
                activeSitePath = createdSite?.path || nextSlug;
                persistedSite = {
                    ...(persistedSite || {}),
                    ...(createdSite || {}),
                };
            } else {
                const updateResponse = await apiFetch(`/my-sites/${encodeURIComponent(EditorView.state.sitePath)}`, {
                    method: "PUT",
                    body: buildUpdatePayload({
                        siteName: nextSiteName,
                        slug: nextSlug,
                        currentData: EditorView.state.currentData,
                        selectedTemplate,
                    }),
                });

                const updatedSite = await ensureOk(updateResponse, "Nao foi possivel atualizar o site.");
                activeSitePath = updatedSite?.path || nextSlug;
                persistedSite = {
                    ...(persistedSite || {}),
                    ...(updatedSite || {}),
                };
            }

            const homeDescriptor = {
                route: "/",
                slug: "home",
                title: nextSiteName,
                body: "",
            };

            await upsertSitePage(activeSitePath, {
                ...homeDescriptor,
                title: nextSiteName,
                content: cloneValue(EditorView.state.currentData),
            });

            try {
                const pageDescriptors = await buildTemplatePageDescriptors(templateBasePreviewUrl);
                for (const page of pageDescriptors) {
                    if (page.slug === "home") continue;
                    try {
                        await upsertSitePage(activeSitePath, {
                            ...page,
                            content: {
                                route: page.route,
                                source: "template",
                            },
                        });
                    } catch (pageError) {
                        console.warn(`Nao foi possivel persistir a pagina ${page.slug}:`, pageError);
                    }
                }
            } catch (pageDiscoveryError) {
                console.warn("Nao foi possivel indexar as paginas adicionais do template:", pageDiscoveryError);
            }

            EditorView.state.mode = "site";
            EditorView.state.sitePath = activeSitePath;
            EditorView.state.slug = activeSitePath;
            slugInput.value = activeSitePath;
            siteName = nextSiteName;
            siteSlug = activeSitePath;
            isNewMode = false;
            isExistingSiteMode = true;
            currentSite = {
                ...(persistedSite || {}),
                name: nextSiteName,
                path: activeSitePath,
                published: Boolean(persistedSite?.published),
                extra: {
                    ...(persistedSite?.extra || {}),
                    editor_content: cloneValue(EditorView.state.currentData),
                },
            };
            if (window.location.hash !== `#editor/site/${encodeURIComponent(activeSitePath)}`) {
                history.replaceState(null, "", `#editor/site/${encodeURIComponent(activeSitePath)}`);
            }

            lastSavedSnapshot = createEditorSnapshot(nextSiteName, activeSitePath, EditorView.state.currentData);

            return {
                activeSitePath,
                siteName: nextSiteName,
                siteSlug: activeSitePath,
                site: currentSite,
            };
        };

        btnSave.onclick = async () => {
            const { unsavedChangesCount } = getPendingCounts();
            const saveBusyLabel =
                unsavedChangesCount > 0
                    ? `A guardar ${formatCountLabel(unsavedChangesCount, "alteracao", "alteracoes")}...`
                    : "A guardar...";

            updateActionButtons({ saveBusy: true, saveLabel: saveBusyLabel });
            showEditorStatus(saveBusyLabel, "neutral");

            try {
                await persistDraft();
                showEditorStatus("Rascunho guardado com sucesso.", "success");
            } catch (error) {
                console.error("Erro ao guardar rascunho:", error);
                showEditorStatus("Erro ao guardar rascunho.", "error");
                alert(error.message || "Erro ao guardar o rascunho.");
            } finally {
                updateActionButtons({ clearSaveBusy: true });
            }
        };

        if (btnPublish) {
            btnPublish.onclick = async () => {
                const { unpublishedChangesCount } = getPendingCounts();
                const publishBusyLabel =
                    unpublishedChangesCount > 0
                        ? `A publicar ${formatCountLabel(unpublishedChangesCount, "atualizacao", "atualizacoes")}...`
                        : "A publicar...";

                updateActionButtons({ publishBusy: true, publishLabel: publishBusyLabel });
                showEditorStatus(publishBusyLabel, "neutral");

                try {
                    const { activeSitePath } = await persistDraft();
                    const publishedSite = await publishSite(activeSitePath);
                    currentSite = {
                        ...(currentSite || {}),
                        ...(publishedSite || {}),
                        path: publishedSite?.path || activeSitePath,
                        published: true,
                    };
                    lastPublishedSnapshot = cloneValue(lastSavedSnapshot);

                    showEditorStatus("Site publicado com sucesso.", "success");
                } catch (error) {
                    console.error("Erro ao publicar site:", error);
                    showEditorStatus("Erro ao publicar site.", "error");
                    alert(error.message || "Erro ao publicar o site.");
                } finally {
                    updateActionButtons({ clearPublishBusy: true });
                }
            };
        }

        EditorView._cleanup = () => {
            window.removeEventListener("message", handleMessage);
        };
    },
};
